import { useEffect, useMemo, useRef, useState } from 'react'
import { newHttpService } from '../../services/new-http-service'
import { GlueTriggerTable, groupTriggers, triggerStatus } from '../../cmps/glue-trigger/glue-trigger-table'
import { GlueTriggerFormModal } from '../../cmps/glue-trigger/glue-trigger-form-modal'
import { GlueTriggerAuditHistoryModal } from '../../cmps/glue-trigger/glue-trigger-audit-history-modal'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { GlueTriggerResult } from '../../cmps/glue-trigger/glue-trigger-result'
import { useCachedFetch } from '../../hooks/use-cached-fetch'

const AUDIT_DB_NAME = 'auditech'
const isDeleting = (trigger: any) => (trigger.aws_state || trigger.state) === 'DELETING'

export const GlueTriggersPage: React.FC = () => {
    const { data, isLoading, hasError, refetch, silentRefetch } = useCachedFetch(
        'glue-triggers',
        async () => {
            const result = await newHttpService.get('/api/backoffice/glue-schedules')
            return result.schedules as any[]
        },
        { reportRefreshErrors: true }
    )

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [triggerToEdit, setTriggerToEdit] = useState<any>(null)
    const [historyTriggerName, setHistoryTriggerName] = useState<string | null>(null)

    const [mutationResult, setMutationResult] = useState<any>(null)
    const pendingDeletionNamesRef = useRef<string[]>([])
    const [unknownDeletionNames, setUnknownDeletionNames] = useState<string[]>([])
    const [verificationFailed, setVerificationFailed] = useState(false)
    useEffect(() => { setVerificationFailed(false) }, [data])

    const deletionNames: string[] = useMemo(() => {
        const detail = mutationResult?.detail || mutationResult
        return detail?.deletion_requested || []
    }, [mutationResult])
    const hasPendingDeletions = (data || []).some((trigger: any) =>
        isDeleting(trigger) || deletionNames.includes(trigger.trigger_name)) || unknownDeletionNames.length > 0
    useEffect(() => {
        if (!hasPendingDeletions || isLoading || hasError || !silentRefetch) return
        const refreshSilently = silentRefetch
        let stopped = false
        let timer: ReturnType<typeof setTimeout>
        let confirmedPolls = 0
        const poll = async () => {
            let accepted = false
            const result = await refreshSilently((schedules: any[]) => {
                confirmedPolls += 1
                const unknownStillPresent = schedules.filter((trigger: any) => unknownDeletionNames.includes(trigger.trigger_name))
                const unknownResolved = unknownStillPresent.length === 0 || (confirmedPolls >= 5 &&
                    unknownStillPresent.every((trigger: any) => ['ACTIVATED', 'DEACTIVATED'].includes(triggerStatus(trigger))))
                accepted = unknownResolved && !schedules.some((trigger: any) =>
                    isDeleting(trigger) || deletionNames.includes(trigger.trigger_name))
                return accepted
            })
            if (!stopped && unknownDeletionNames.length > 0 && result === undefined) setVerificationFailed(true)
            if (accepted && !stopped) {
                setVerificationFailed(false)
                setUnknownDeletionNames([])
            }
            if (!stopped) timer = setTimeout(poll, 3000)
        }
        timer = setTimeout(poll, 3000)
        return () => {
            stopped = true
            clearTimeout(timer)
        }
    }, [deletionNames, hasPendingDeletions, isLoading, hasError, silentRefetch, unknownDeletionNames])

    const grouped = useMemo(() => groupTriggers(data || []), [data])
    const inactiveSchedules = grouped.filter((t: any) => t.state === 'DEACTIVATED')
    const partiallyActiveSchedules = grouped.filter((t: any) => t.state === 'MIXED')

    const openCreateModal = () => {
        setTriggerToEdit(null)
        setIsModalOpen(true)
    }

    const openEditModal = (trigger: any) => {
        if ((trigger._grouped_triggers || [trigger]).some((member: any) => !['ACTIVATED', 'DEACTIVATED'].includes(triggerStatus(member)))) {
            setMutationResult({ failed: true, detail: 'AWS triggers are changing or their state is unknown. Refresh before editing.' })
            return
        }
        setTriggerToEdit(trigger)
        setIsModalOpen(true)
    }

    const openHistoryModal = (triggerName: string) => {
        setIsModalOpen(false)
        setTriggerToEdit(null)
        setHistoryTriggerName(triggerName)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setTriggerToEdit(null)
    }

    const handleSuccess = () => {
        closeModal()
        if (pendingDeletionNamesRef.current.length === 0) refetch()
    }

    const handleResult = (result: any) => {
        const detail = result?.detail || result
        setVerificationFailed(false)
        pendingDeletionNamesRef.current = [...(detail?.deletion_requested || []), ...(detail?.deletion_unknown || [])]
        setUnknownDeletionNames(detail?.deletion_unknown || [])
        setMutationResult(result)
    }

    return <div className="tablePageContainer">
        <div className="page-header">
            <p className="tableSubtitle">View and manage ETL schedules.</p>
            <div className="page-header-actions">
                <button className="refresh-btn" onClick={refetch} disabled={isLoading} title="Refresh">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 4 23 10 17 10" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                </button>
                <button className="add-btn" onClick={openCreateModal}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create Schedule
                </button>
            </div>
        </div>

        {mutationResult?.failed && <GlueTriggerResult result={mutationResult} />}
        {!isLoading && !hasError && !verificationFailed && partiallyActiveSchedules.length > 0 && <div role="status" className="inactive-triggers-warning">
            <strong>{partiallyActiveSchedules.length} schedule group{partiallyActiveSchedules.length > 1 ? 's are' : ' is'} partially active.</strong>
            {' '}Some triggers will run while others are inactive.
        </div>}
        {!isLoading && !hasError && !verificationFailed && inactiveSchedules.length > 0 && (
            <div className="inactive-triggers-warning">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <span>
                    <strong>{inactiveSchedules.length} schedule{inactiveSchedules.length > 1 ? 's are' : ' is'} not activated.</strong>
                    {' '}Inactive schedules will not run.
                </span>
            </div>
        )}

        {isLoading && <Spinner />}
        {!isLoading && !hasError && !verificationFailed && (
            <GlueTriggerTable
                data={data || []}
                onEditClick={openEditModal}
            />
        )}
        {!isLoading && (hasError || verificationFailed) && <div role="alert" className="error-hint">Could not refresh schedules from AWS. The current state is unconfirmed. Refresh before editing or retrying a save.</div>}

        {isModalOpen && <GlueTriggerFormModal
            key={triggerToEdit?.trigger_name || 'create'}
            companyName={AUDIT_DB_NAME}
            triggerToEdit={triggerToEdit}
            onSuccess={handleSuccess}
            onResult={handleResult}
            onClose={closeModal}
            onOpenAuditHistory={openHistoryModal}
        />}

        {historyTriggerName && (
            <GlueTriggerAuditHistoryModal
                isOpen={true}
                companyName={AUDIT_DB_NAME}
                triggerName={historyTriggerName}
                onClose={() => setHistoryTriggerName(null)}
            />
        )}
    </div>
}
